import React, { useEffect, useState, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Store, Shield, ArrowRight, Layers, Loader2, UtensilsCrossed, ImageIcon, CheckCircle2, XCircle, MessageCircle, Eye, EyeOff, Clock, AlertCircle, Inbox, Check, X, FileText, BarChart3, Receipt, LogOut, UserIcon, ChefHat } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { useIdleLogout } from '../../lib/useIdleLogout';
interface RestaurantApplication {
  id: string;
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string | null;
  business_type: string;
  uses_whatsapp_orders: boolean;
  has_digital_menu: boolean;
  main_goal: string | null;
  status: 'pending' | 'contacted' | 'approved' | 'rejected';
  created_at: string;
}
const ENROLLMENT_OPTIONS = [{
  title: 'Conductor',
  description: 'Registrar conductor, crear o reutilizar vehículo y dejar accesos listos',
  route: '/admin/drivers/new',
  icon: Car,
  accent: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  iconBg: 'bg-blue-500/15',
  btnClass: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
}, {
  title: 'Subir restaurante',
  description: 'Registrar restaurante, crear acceso inicial y dejarlo listo para operar',
  route: '/admin/enrollment/restaurant',
  icon: Store,
  accent: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  iconBg: 'bg-purple-500/15',
  btnClass: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
}, {
  title: 'Generar Recibo',
  description: 'Crear recibos de pago profesionales para clientes, imprimir o compartir por WhatsApp',
  route: '/admin/enrollment/receipts',
  icon: Receipt,
  accent: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  iconBg: 'bg-emerald-500/15',
  btnClass: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
}, {
  title: 'Admin de NOIZA',
  description: 'Crear usuario interno de NOIZA y asignar permisos de plataforma',
  route: '/admin/enrollment/platform-admin',
  icon: Shield,
  accent: 'bg-[#006847]/10 border-[#006847]/20 text-[#00a870]',
  iconBg: 'bg-[#006847]/20',
  btnClass: 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30'
}, {
  title: 'Actualizar menú',
  description: 'Agregar categorías, productos u opciones a un restaurante existente sin recrearlo',
  route: '/admin/enrollment/menu-update',
  icon: UtensilsCrossed,
  accent: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  iconBg: 'bg-amber-500/15',
  btnClass: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
  btnLabel: 'Iniciar actualización'
}, {
  title: 'Subir imágenes del menú',
  description: 'Relaciona imágenes con productos del menú usando el nombre del archivo',
  route: '/admin/enrollment/menu-images',
  icon: ImageIcon,
  accent: 'bg-teal-500/10 border-teal-500/20 text-teal-400',
  iconBg: 'bg-teal-500/15',
  btnClass: 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30',
  btnLabel: 'Abrir módulo'
}, {
  title: 'Métricas NOIZA',
  description: 'Revisa métricas globales, crecimiento y operación de NOIZA',
  route: '/admin/enrollment/metrics',
  icon: BarChart3,
  accent: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  iconBg: 'bg-rose-500/15',
  btnClass: 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30',
  btnLabel: 'Ver métricas'
}];
export function EnrollmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // --- Access Guard State ---
  const [guardState, setGuardState] = useState<'loading' | 'authorized' | 'redirecting'>('loading');
  // --- Idle Logout ---
  const {
    showWarning: showIdleWarning
  } = useIdleLogout({
    enabled: guardState === 'authorized',
    timeoutMs: 900000,
    warningBeforeMs: 60000,
    redirectTo: '/admin/login'
  });
  // --- Active Restaurants State ---
  const [restaurants, setRestaurants] = useState<{
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    show_in_marketplace: boolean;
  }[]>([]);
  const [restLoading, setRestLoading] = useState(true);
  const [togglingVisibilityId, setTogglingVisibilityId] = useState<string | null>(null);
  // --- Applications State ---
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'approved' | 'rejected'>('all');
  const [selectedApp, setSelectedApp] = useState<RestaurantApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [showSessionInfo, setShowSessionInfo] = useState(false);
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', {
      replace: true
    });
  };
  const fetchApplications = async () => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_applications').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setApplications(data || []);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setAppsError(err.message);
    } finally {
      setAppsLoading(false);
    }
  };
  // ─── Access Guard ─────────────────────────────────────────────
  useEffect(() => {
    async function guard() {
      try {
        // 1. Check authenticated user
        const {
          data: {
            user
          },
          error: userError
        } = await supabase.auth.getUser();
        if (userError || !user) {
          console.log('[EnrollmentPage] No authenticated user — redirecting to /admin/login');
          setGuardState('redirecting');
          navigate('/admin/login', {
            state: {
              from: location.pathname
            },
            replace: true
          });
          return;
        }
        // 2. Check platform_admins table first (primary source)
        let accessRole: string | null = null;
        const {
          data: platformData,
          error: platformError
        } = await supabase.from('platform_admins').select('role').eq('user_id', user.id).eq('is_active', true).maybeSingle();
        if (!platformError && platformData && (platformData.role === 'super_admin' || platformData.role === 'platform_admin')) {
          accessRole = platformData.role;
        }
        // 3. Fallback: check restaurant_users table
        if (!accessRole) {
          const {
            data: roleData,
            error: roleError
          } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
          if (roleError) {
            console.error('[EnrollmentPage] Role check error:', roleError);
          }
          if (roleData) {
            accessRole = roleData.role;
          }
        }
        if (!accessRole) {
          console.log('[EnrollmentPage] User lacks required role — redirecting to /');
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        console.log('[EnrollmentPage] Access granted:', {
          email: user.email,
          role: accessRole
        });
        setSessionEmail(user.email || null);
        setSessionUserId(user.id);
        setGuardState('authorized');
      } catch (err) {
        console.error('[EnrollmentPage] Guard error:', err);
        setGuardState('redirecting');
        navigate('/admin/login', {
          replace: true
        });
      }
    }
    guard();
  }, [navigate, location.pathname]);
  const fetchRestaurants = async () => {
    setRestLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').select('id, name, slug, logo_url, show_in_marketplace').eq('is_active', true).order('name', {
        ascending: true
      });
      if (error) throw error;
      setRestaurants(data || []);
    } catch (err: any) {
      console.error('Error fetching restaurants:', err);
    } finally {
      setRestLoading(false);
    }
  };
  const toggleMarketplaceVisibility = async (restaurantId: string, currentValue: boolean) => {
    setTogglingVisibilityId(restaurantId);
    const newValue = !currentValue;
    // Optimistic update
    setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
      ...r,
      show_in_marketplace: newValue
    } : r));
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').update({
        show_in_marketplace: newValue
      }).eq('id', restaurantId).select('id, show_in_marketplace');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('La actualización fue bloqueada por RLS. Corré FIX_RLS_RESTAURANTS_MARKETPLACE_TOGGLE.sql en Supabase.');
      }
      setToast({
        message: newValue ? 'Restaurante visible en el marketplace' : 'Restaurante oculto del marketplace',
        type: 'success'
      });
    } catch (err: any) {
      console.error('Error toggling marketplace visibility:', err);
      // Revert on error
      setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
        ...r,
        show_in_marketplace: currentValue
      } : r));
      setToast({
        message: 'No se pudo actualizar la visibilidad',
        type: 'error'
      });
    } finally {
      setTogglingVisibilityId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  useEffect(() => {
    if (guardState === 'authorized') {
      fetchApplications();
      fetchRestaurants();
    }
  }, [guardState]);
  const updateStatus = async (id: string, newStatus: 'pending' | 'contacted' | 'approved' | 'rejected') => {
    setUpdatingId(id);
    try {
      const {
        error
      } = await supabase.from('restaurant_applications').update({
        status: newStatus
      }).eq('id', id);
      if (error) throw error;
      setToast({
        message: 'Estado actualizado correctamente',
        type: 'success'
      });
      await fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({
          ...selectedApp,
          status: newStatus
        });
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setToast({
        message: 'Error al actualizar estado',
        type: 'error'
      });
    } finally {
      setUpdatingId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  const handleWhatsApp = (whatsapp: string) => {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    if (cleanNumber) {
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };
  const filteredApps = applications.filter(app => statusFilter === 'all' || app.status === statusFilter);
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20" data-id="element-3068">
            Pendiente
          </span>;
      case 'contacted':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20" data-id="element-3069">
            Contactado
          </span>;
      case 'approved':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" data-id="element-3070">
            Aprobado
          </span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/20" data-id="element-3071">
            Rechazado
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-500/10 text-gray-400 border border-gray-500/20" data-id="element-3072">
            {status}
          </span>;
    }
  };
  if (guardState !== 'authorized') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-3073">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-3074" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-3075">
      {/* Idle Warning Banner */}
      <AnimatePresence data-id="element-3076">
        {showIdleWarning && <motion.div initial={{
        y: -60,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -60,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed top-0 left-0 right-0 z-[70] flex items-center justify-center px-4 py-3 bg-amber-500/90 text-[#0B0B0B]" data-id="element-3077">
            <Clock className="w-4 h-4 mr-2 shrink-0" data-id="element-3078" />
            <span className="text-sm font-bold" data-id="element-3079">
              Por seguridad, tu sesión se cerrará pronto por inactividad.
            </span>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-id="element-3080">
        {/* Header */}
        <div className="flex items-center justify-between gap-4" data-id="element-3081">
          <div className="flex items-center gap-4" data-id="element-3082">
            <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-3083">
              <Layers className="w-7 h-7 text-[#006847]" data-id="element-3084" />
            </div>
            <div data-id="element-3085">
              <h1 className="text-3xl font-black tracking-tight" data-id="element-3086">
                NOIZA Enrollment
              </h1>
              <p className="text-white/40 text-sm mt-1" data-id="element-3087">
                Gestiona los diferentes tipos de registro y acceso dentro de
                NOIZA
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-3088">
            {/* Session Info Button */}
            <div className="relative" data-id="element-3089">
              <button onClick={() => setShowSessionInfo(!showSessionInfo)} className="flex items-center gap-2 px-3 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-3090">
                <UserIcon className="w-4 h-4" data-id="element-3091" />
                <span className="hidden sm:inline max-w-[160px] truncate" data-id="element-3092">
                  {sessionEmail || 'Usuario'}
                </span>
              </button>

              {/* Session Info Dropdown */}
              <AnimatePresence data-id="element-3093">
                {showSessionInfo && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 top-full mt-2 w-72 bg-[#141414] border border-white/10 rounded-2xl p-4 shadow-2xl z-50" data-id="element-3094">
                    <div className="space-y-3" data-id="element-3095">
                      <div data-id="element-3096">
                        <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-3097">
                          Sesión activa
                        </p>
                        <p className="text-sm font-bold text-white break-all" data-id="element-3098">
                          {sessionEmail || '—'}
                        </p>
                      </div>
                      {sessionUserId && <div data-id="element-3099">
                          <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-3100">
                            User ID
                          </p>
                          <p className="text-[11px] font-mono text-white/50 break-all" data-id="element-3101">
                            {sessionUserId}
                          </p>
                        </div>}
                      <div className="pt-2 border-t border-white/5" data-id="element-3102">
                        <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl text-sm font-bold transition-colors" data-id="element-3103">
                          <LogOut className="w-4 h-4" data-id="element-3104" />
                          Cerrar sesión
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Logout Button */}
            <button onClick={handleSignOut} className="p-2.5 bg-[#141414] border border-white/10 rounded-xl text-white/50 hover:text-red-400 hover:border-red-500/20 transition-colors" title="Cerrar sesión" data-id="element-3105">
              <LogOut className="w-5 h-5" data-id="element-3106" />
            </button>
          </div>
        </div>

        {/* Question */}
        <p className="text-white/50 text-lg font-medium" data-id="element-3107">
          ¿Qué deseas registrar hoy?
        </p>

        {/* Cards */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3108">
          {ENROLLMENT_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return <div key={opt.route} className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-3109">
                <div className="space-y-4" data-id="element-3110">
                  <div className={`w-12 h-12 ${opt.iconBg} rounded-xl flex items-center justify-center`} data-id="element-3111">
                    <Icon className="w-6 h-6" data-id="element-3112" />
                  </div>
                  <div data-id="element-3113">
                    <h2 className="text-lg font-bold text-white" data-id="element-3114">
                      {opt.title}
                    </h2>
                    <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-3115">
                      {opt.description}
                    </p>
                  </div>
                </div>

                <button onClick={() => navigate(opt.route)} className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${opt.btnClass}`} data-id="element-3116">
                  {(opt as any).btnLabel || 'Iniciar enrollment'}
                  <ArrowRight className="w-4 h-4" data-id="element-3117" />
                </button>
              </div>;
        })}
        </div>

        {/* ═══ COMPACT APPLICATIONS CARD ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-3118">
          <div className="space-y-4" data-id="element-3119">
            <div className="flex items-start justify-between" data-id="element-3120">
              <div className="w-12 h-12 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-3121">
                <FileText className="w-6 h-6 text-orange-400" data-id="element-3122" />
              </div>
              {stats.pending > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse" data-id="element-3123">
                  {stats.pending} pendiente{stats.pending !== 1 ? 's' : ''}
                </span>}
            </div>
            <div data-id="element-3124">
              <h2 className="text-lg font-bold text-white" data-id="element-3125">
                Aplicaciones de restaurantes
              </h2>
              <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-3126">
                Revisa las solicitudes de ingreso a NOIZA
              </p>
            </div>

            {/* Compact Stats Row */}
            <div className="flex flex-wrap gap-3 pt-2" data-id="element-3127">
              <div className="flex items-center gap-1.5" data-id="element-3128">
                <div className="w-2 h-2 rounded-full bg-white/30" data-id="element-3129" />
                <span className="text-xs text-white/50" data-id="element-3130">
                  <span className="font-bold text-white" data-id="element-3131">{stats.total}</span>{' '}
                  total
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-3132">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-3133" />
                <span className="text-xs text-white/50" data-id="element-3134">
                  <span className="font-bold text-amber-500" data-id="element-3135">
                    {stats.pending}
                  </span>{' '}
                  pendientes
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-3136">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-3137" />
                <span className="text-xs text-white/50" data-id="element-3138">
                  <span className="font-bold text-blue-500" data-id="element-3139">
                    {stats.contacted}
                  </span>{' '}
                  contactados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-3140">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-3141" />
                <span className="text-xs text-white/50" data-id="element-3142">
                  <span className="font-bold text-emerald-500" data-id="element-3143">
                    {stats.approved}
                  </span>{' '}
                  aprobados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-3144">
                <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-3145" />
                <span className="text-xs text-white/50" data-id="element-3146">
                  <span className="font-bold text-red-500" data-id="element-3147">
                    {stats.rejected}
                  </span>{' '}
                  rechazados
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => setAppsModalOpen(true)} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" data-id="element-3148">
            Ver aplicaciones
            <ArrowRight className="w-4 h-4" data-id="element-3149" />
          </button>
        </div>

        {/* ═══ ACCESO RÁPIDO KDS ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors" data-id="element-3150">
          <div className="space-y-4" data-id="element-3151">
            <div className="flex items-start justify-between" data-id="element-3152">
              <div className="flex items-center gap-3" data-id="element-3153">
                <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center" data-id="element-3154">
                  <ChefHat className="w-6 h-6 text-cyan-400" data-id="element-3155" />
                </div>
                <div data-id="element-3156">
                  <h2 className="text-lg font-bold text-white" data-id="element-3157">
                    Acceso rápido KDS
                  </h2>
                  <p className="text-sm text-white/40 mt-0.5" data-id="element-3158">
                    Ir directo a la cocina de cualquier restaurante
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" data-id="element-3159">
                {restaurants.length} activos
              </span>
            </div>

            {restLoading ? <div className="flex items-center justify-center py-6" data-id="element-3160">
                <Loader2 className="w-6 h-6 text-white/20 animate-spin" data-id="element-3161" />
              </div> : restaurants.length === 0 ? <p className="text-white/30 text-sm text-center py-4" data-id="element-3162">
                No hay restaurantes activos
              </p> : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 pt-2" data-id="element-3163">
                {restaurants.map(r => {
              const isHidden = r.show_in_marketplace === false;
              const isToggling = togglingVisibilityId === r.id;
              return <div key={r.id} className="relative flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-colors group" data-id="element-3164">
                      <button onClick={() => navigate(`/admin/${r.slug}/kds`)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-id="element-3165">
                        <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden shrink-0 flex items-center justify-center" data-id="element-3166">
                          {r.logo_url ? <img src={r.logo_url} alt={r.name} className="w-full h-full object-cover" data-id="element-3167" /> : <Store className="w-5 h-5 text-white/30" data-id="element-3168" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-3169">
                          <div className="font-bold text-white text-sm truncate group-hover:text-cyan-300 transition-colors flex items-center gap-1.5" data-id="element-3170">
                            {r.name}
                            {isHidden && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0" data-id="element-3171">
                                OCULTO
                              </span>}
                          </div>
                          <div className="text-xs text-white/30 truncate" data-id="element-3172">
                            {r.slug}
                          </div>
                        </div>
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  toggleMarketplaceVisibility(r.id, r.show_in_marketplace !== false);
                }} disabled={isToggling} title={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} aria-label={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} className={`p-2 rounded-lg border transition-colors shrink-0 ${isHidden ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'} ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-3173">
                        {isToggling ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-3174" /> : isHidden ? <EyeOff className="w-4 h-4" data-id="element-3175" /> : <Eye className="w-4 h-4" data-id="element-3176" />}
                      </button>
                    </div>;
            })}
              </div>}
          </div>
        </div>

        {/* ═══ DOCUMENTOS LEGALES ═══ */}
        <div className="pt-4 border-t border-white/10" data-id="element-3177">
          <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-3178">
            Documentos legales
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-3179">
            <a href="/terminos-restaurantes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-3180">
              <FileText className="w-4 h-4 text-[#006847]" data-id="element-3181" />
              T&C Restaurantes
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-3182" />
            </a>
            <a href="/terminos-comensales" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-3183">
              <FileText className="w-4 h-4 text-blue-400" data-id="element-3184" />
              T&C Comensales
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-3185" />
            </a>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ═══ FULL-SCREEN APPLICATIONS MANAGER MODAL ═══ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-3186">
        {appsModalOpen && <motion.div className="fixed inset-0 z-50 flex items-stretch justify-end" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-3187">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAppsModalOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3188" />

            {/* Slide-over Panel */}
            <motion.div className="relative w-full sm:w-[640px] lg:w-[780px] h-full bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col overflow-hidden" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} data-id="element-3189">
              {/* Panel Header */}
              <div className="shrink-0 px-6 py-5 border-b border-white/10 bg-[#0B0B0B]" data-id="element-3190">
                <div className="flex items-center justify-between" data-id="element-3191">
                  <div className="flex items-center gap-3" data-id="element-3192">
                    <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-3193">
                      <FileText className="w-5 h-5 text-orange-400" data-id="element-3194" />
                    </div>
                    <div data-id="element-3195">
                      <h2 className="text-xl font-bold text-white" data-id="element-3196">
                        Aplicaciones de restaurantes
                      </h2>
                      <p className="text-white/40 text-xs mt-0.5" data-id="element-3197">
                        Centro de control de solicitudes
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setAppsModalOpen(false)} className="p-2 -mr-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-id="element-3198">
                    <X className="w-6 h-6" data-id="element-3199" />
                  </button>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-5 gap-3 mt-5" data-id="element-3200">
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-3 text-center" data-id="element-3201">
                    <span className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-3202">
                      Total
                    </span>
                    <span className="text-xl font-black text-white" data-id="element-3203">
                      {stats.total}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-amber-500/20 rounded-xl p-3 text-center" data-id="element-3204">
                    <span className="text-amber-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-3205">
                      Pendientes
                    </span>
                    <span className="text-xl font-black text-amber-500" data-id="element-3206">
                      {stats.pending}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-blue-500/20 rounded-xl p-3 text-center" data-id="element-3207">
                    <span className="text-blue-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-3208">
                      Contactados
                    </span>
                    <span className="text-xl font-black text-blue-500" data-id="element-3209">
                      {stats.contacted}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-emerald-500/20 rounded-xl p-3 text-center" data-id="element-3210">
                    <span className="text-emerald-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-3211">
                      Aprobados
                    </span>
                    <span className="text-xl font-black text-emerald-500" data-id="element-3212">
                      {stats.approved}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-red-500/20 rounded-xl p-3 text-center" data-id="element-3213">
                    <span className="text-red-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-3214">
                      Rechazados
                    </span>
                    <span className="text-xl font-black text-red-500" data-id="element-3215">
                      {stats.rejected}
                    </span>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex overflow-x-auto hide-scrollbar gap-2 mt-4 pb-1" data-id="element-3216">
                  {[{
                id: 'all',
                label: 'Todos'
              }, {
                id: 'pending',
                label: 'Pendientes'
              }, {
                id: 'contacted',
                label: 'Contactados'
              }, {
                id: 'approved',
                label: 'Aprobados'
              }, {
                id: 'rejected',
                label: 'Rechazados'
              }].map(tab => <button key={tab.id} onClick={() => setStatusFilter(tab.id as any)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${statusFilter === tab.id ? 'bg-white text-[#0B0B0B]' : 'bg-[#141414] text-white/60 border border-white/10 hover:bg-white/5 hover:text-white'}`} data-id="element-3217">
                      {tab.label}
                    </button>)}
                </div>
              </div>

              {/* Panel Content — Scrollable */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-3218">
                {appsLoading ? <div className="space-y-4" data-id="element-3219">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#141414] rounded-2xl border border-white/5 animate-pulse" data-id="element-3220" />)}
                  </div> : appsError ? <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center" data-id="element-3221">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" data-id="element-3222" />
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-3223">
                      Error al cargar aplicaciones
                    </h3>
                    <p className="text-white/60 text-sm mb-4" data-id="element-3224">{appsError}</p>
                    <button onClick={fetchApplications} className="px-6 py-2 bg-red-500/20 text-red-400 rounded-xl font-bold text-sm hover:bg-red-500/30 transition-colors" data-id="element-3225">
                      Reintentar
                    </button>
                  </div> : filteredApps.length === 0 ? <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-3226">
                    <Inbox className="w-12 h-12 text-white/20 mx-auto mb-4" data-id="element-3227" />
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-3228">
                      Aún no hay aplicaciones
                    </h3>
                    <p className="text-white/50 text-sm" data-id="element-3229">
                      Cuando un restaurante aplique a NOIZA, aparecerá aquí.
                    </p>
                  </div> : <div className="space-y-4" data-id="element-3230">
                    {filteredApps.map(app => <div key={app.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors relative overflow-hidden" data-id="element-3231">
                        {updatingId === app.id && <div className="absolute inset-0 bg-[#141414]/80 backdrop-blur-sm z-10 flex items-center justify-center" data-id="element-3232">
                            <Loader2 className="w-6 h-6 text-white animate-spin" data-id="element-3233" />
                          </div>}

                        <div className="flex flex-col gap-4" data-id="element-3234">
                          {/* Row 1: Name + Status + Date */}
                          <div className="flex items-start justify-between gap-3" data-id="element-3235">
                            <div data-id="element-3236">
                              <div className="flex items-center gap-2 mb-1" data-id="element-3237">
                                <h3 className="font-bold text-lg text-white" data-id="element-3238">
                                  {app.restaurant_name}
                                </h3>
                                {getStatusBadge(app.status)}
                              </div>
                              <p className="text-sm text-white/60" data-id="element-3239">
                                {app.owner_name} • {app.business_type}
                              </p>
                            </div>
                            <p className="text-xs text-white/40 shrink-0" data-id="element-3240">
                              {new Date(app.created_at).toLocaleDateString('es-CR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </p>
                          </div>

                          {/* Row 2: Quick Info */}
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-3241">
                            <div className="flex items-center gap-2" data-id="element-3242">
                              <span className="text-white/40" data-id="element-3243">WhatsApp:</span>
                              <span className="text-white font-medium" data-id="element-3244">
                                {app.whatsapp}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-3245">
                              <span className="text-white/40" data-id="element-3246">
                                Menú digital:
                              </span>
                              <span className="text-white font-medium" data-id="element-3247">
                                {app.has_digital_menu ? 'Sí' : 'No'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-3248">
                              <span className="text-white/40" data-id="element-3249">Pedidos WA:</span>
                              <span className="text-white font-medium" data-id="element-3250">
                                {app.uses_whatsapp_orders ? 'Sí' : 'No'}
                              </span>
                            </div>
                          </div>

                          {/* Row 3: Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5" data-id="element-3251">
                            <button onClick={() => {
                      setSelectedApp(app);
                      setDetailOpen(true);
                    }} className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-1.5" data-id="element-3252">
                              <Eye className="w-4 h-4" data-id="element-3253" /> Ver detalle
                            </button>
                            <button onClick={() => handleWhatsApp(app.whatsapp)} disabled={!app.whatsapp} className="px-3 py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5" data-id="element-3254">
                              <MessageCircle className="w-4 h-4" data-id="element-3255" /> WhatsApp
                            </button>

                            <div className="flex-1" data-id="element-3256" />

                            {app.status === 'pending' && <button onClick={() => updateStatus(app.id, 'contacted')} className="px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm font-bold rounded-xl transition-colors" data-id="element-3257">
                                Contactado
                              </button>}
                            {app.status !== 'approved' && <button onClick={() => updateStatus(app.id, 'approved')} className="px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded-xl transition-colors" data-id="element-3258">
                                Aprobar
                              </button>}
                            {app.status !== 'rejected' && <button onClick={() => updateStatus(app.id, 'rejected')} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-colors" data-id="element-3259">
                                Rechazar
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedApp && <Modal isOpen={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle de Aplicación" data-id="element-3260">
          <div className="space-y-6" data-id="element-3261">
            <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-3262">
              <div data-id="element-3263">
                <h3 className="text-2xl font-bold text-white" data-id="element-3264">
                  {selectedApp.restaurant_name}
                </h3>
                <p className="text-white/60" data-id="element-3265">{selectedApp.owner_name}</p>
              </div>
              {getStatusBadge(selectedApp.status)}
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-3266">
              <div data-id="element-3267">
                <p className="text-white/40 text-sm mb-1" data-id="element-3268">WhatsApp</p>
                <p className="text-white font-medium" data-id="element-3269">{selectedApp.whatsapp}</p>
              </div>
              <div data-id="element-3270">
                <p className="text-white/40 text-sm mb-1" data-id="element-3271">Instagram/Facebook</p>
                <p className="text-white font-medium" data-id="element-3272">
                  {selectedApp.social_handle || '-'}
                </p>
              </div>
              <div data-id="element-3273">
                <p className="text-white/40 text-sm mb-1" data-id="element-3274">Tipo de negocio</p>
                <p className="text-white font-medium" data-id="element-3275">
                  {selectedApp.business_type}
                </p>
              </div>
              <div data-id="element-3276">
                <p className="text-white/40 text-sm mb-1" data-id="element-3277">Fecha de solicitud</p>
                <p className="text-white font-medium" data-id="element-3278">
                  {new Date(selectedApp.created_at).toLocaleString('es-CR')}
                </p>
              </div>
              <div data-id="element-3279">
                <p className="text-white/40 text-sm mb-1" data-id="element-3280">
                  ¿Usa pedidos por WhatsApp?
                </p>
                <p className="text-white font-medium" data-id="element-3281">
                  {selectedApp.uses_whatsapp_orders ? 'Sí' : 'No'}
                </p>
              </div>
              <div data-id="element-3282">
                <p className="text-white/40 text-sm mb-1" data-id="element-3283">
                  ¿Tiene menú digital?
                </p>
                <p className="text-white font-medium" data-id="element-3284">
                  {selectedApp.has_digital_menu ? 'Sí' : 'No'}
                </p>
              </div>
            </div>

            <div data-id="element-3285">
              <p className="text-white/40 text-sm mb-1" data-id="element-3286">Objetivo principal</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-3287">
                <p className="text-white/90" data-id="element-3288">
                  {selectedApp.main_goal || 'No especificado'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10" data-id="element-3289">
              <button onClick={() => handleWhatsApp(selectedApp.whatsapp)} className="flex-1 py-3 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-3290">
                <MessageCircle className="w-5 h-5" data-id="element-3291" /> WhatsApp
              </button>

              {selectedApp.status === 'pending' && <button onClick={() => updateStatus(selectedApp.id, 'contacted')} className="flex-1 py-3 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-xl font-bold transition-colors" data-id="element-3292">
                  Marcar Contactado
                </button>}
              {selectedApp.status !== 'approved' && <button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 py-3 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-xl font-bold transition-colors" data-id="element-3293">
                  Aprobar
                </button>}
              {selectedApp.status !== 'rejected' && <button onClick={() => updateStatus(selectedApp.id, 'rejected')} className="flex-1 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-bold transition-colors" data-id="element-3294">
                  Rechazar
                </button>}
            </div>
          </div>
        </Modal>}

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-3295">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} backdrop-blur-md`} data-id="element-3296">
            {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5" data-id="element-3297" /> : <AlertCircle className="w-5 h-5" data-id="element-3298" />}
            <span className="font-bold text-sm" data-id="element-3299">{toast.message}</span>
          </div>
        </div>}
    </div>;
}